import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  TreeSelect,
  message
} from 'antd';
import { useSelector } from 'react-redux';
import LevelChip, { type Level } from '../../layout/context/LevelChip';
import PersonPicker, { type PickerValue } from '../people/PersonPicker';
import { selectTreeIndex } from '../../../redux/context/selectors';
import {
  buildPlaceTree,
  buildSummary,
  effectiveRole,
  placesNeedingRole,
  placesRefusingDefault,
  placesWithRow,
  pruneOverrides,
  startingOverride,
  type PlaceTreeNode
} from './inviteSummary';
import ManagedByBanner from '../../UI/ManagedByBanner';
import {
  getRoleNames,
  type RoleName
} from '../../../service/user.service';
import {
  inviteToOrganisation,
  revokeOrganisationInvitation,
  type OrganisationInviteResult
} from '../../../service/invitation.service';
import { grantExceedsFrom403 } from '../../../service/capability.service';
import {
  managedByFrom409,
  type ManagedBy
} from '../../../service/delegation.service';
import { moduleLabel } from '../../../config/capabilities';

/** A place a person can be invited to: the organisation, an entity or a workspace. */
export interface InviteTarget {
  id: number;
  name: string;
  kind: string;
  /** The entity a workspace sits under, shown so same-named workspaces can be told apart. */
  parent?: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The sentence for a failed invitation: the grant-ceiling refusal, else the server's own text. */
const errorText = (e: any): string => {
  const modules = grantExceedsFrom403(e);
  return modules
    ? `You cannot grant more than you hold. Exceeds your access on: ${modules.map(moduleLabel).join(', ') || 'unknown modules'}.`
    : (typeof e?.response?.data?.detail === 'string' && e.response.data.detail) ||
        e?.response?.data?.message ||
        'The invitation could not be sent.';
};

interface InviteForm {
  people: PickerValue[];
  name?: string;
}

export const roleKey = (role: RoleName) =>
  role.custom_role_id != null ? `c:${role.custom_role_id}` : role.name;

export const targetLabel = (target: InviteTarget) => (
  // The same chip the context switcher uses, so a level reads the same everywhere.
  <span className="flex items-center gap-2">
    <LevelChip level={target.kind as Level} compact />
    <span>{target.name}</span>
    {target.parent && <span 
      className="text-gray-400 text-xs"
    >in {target.parent}</span>}
  </span>
);

export const issuedMessage = (
  result: OrganisationInviteResult
): { type: 'success' | 'warning'; text: string } => {
  if (result.status === 'granted') {
    return {
      type: 'success',
      text: 'That person already belongs to this organisation, so the access was granted at once.'
    };
  }
  if (result.delivery === 'failed') {
    return {
      type: 'warning',
      text: 'Invitation created, but email delivery failed. You can resend it later.'
    };
  }
  return { type: 'success', text: 'Invitation sent.' };
};

/**
 * One role picker per "Where" row. The list is the server's own for that node, so it never
 * offers a role the grant ceiling would refuse, and a workspace scopes it to its custom
 * roles. When the location changes, the chosen role is kept if the new list still has it.
 */
export const RoleSelect: React.FC<{
  nodeId?: number;
  value?: string;
  onChange?: (value?: string) => void;
  loadRoles: (nodeId: number) => Promise<RoleName[]>;
}> = ({ nodeId, value, onChange, loadRoles }) => {
  const [roles, setRoles] = useState<RoleName[]>([]);
  const [loading, setLoading] = useState(false);
  // Read through a ref so a parent re-render never refetches or clears the role.
  const current = useRef({ value, onChange });
  current.current = { value, onChange };

  useEffect(() => {
    if (nodeId == null) {
      setRoles([]);
      return undefined;
    }
    let stale = false;
    setLoading(true);
    loadRoles(nodeId).then((list) => {
      if (stale) return;
      setRoles(list);
      setLoading(false);
      const { value: chosen, onChange: change } = current.current;
      if (chosen && !list.some((role) => roleKey(role) === chosen)) change?.(undefined);
    });
    return () => {
      stale = true;
    };
  }, [nodeId, loadRoles]);

  return (
    <Select
      placeholder={nodeId == null ? 'Choose where first' : 'Role'}
      disabled={nodeId == null}
      loading={loading}
      value={value}
      onChange={onChange}
      optionLabelProp="short"
      style={{ width: '100%' }}
      popupMatchSelectWidth={false}
      dropdownStyle={{ minWidth: 320, maxWidth: 420 }}
      options={roles.map((role) => ({
        value: roleKey(role),
        short: role.name,
        label: (
          <div>
            <div>{role.name}</div>
            {role.description && (
              <div 
                className="text-gray-400 text-xs whitespace-normal"
              >{role.description}</div>
            )}
          </div>
        )
      }))}
    />
  );
};

/**
 * The one invitation form: who (people from the picker or typed emails), where (a tree of
 * the places the inviter may grant on) and one role for all of them, with a per-place
 * override. Always sent through the organisation's invitation. For an address with no
 * account the server creates it and mails the person their sign-in; otherwise they get the
 * usual invitation to accept. No password is typed or shown here.
 */
const InviteModal: React.FC<{
  open: boolean;
  onClose: () => void;
  organisationId?: number;
  targets: InviteTarget[];
  title?: string;
  /** Places to tick when the popup opens (the page's own node), if the inviter may grant there. */
  defaultNodeIds?: number[];
  /** Called after the server accepted the invitation, so the caller can refresh its lists. */
  onInvited?: (result: OrganisationInviteResult) => void;
}> = ({ open, onClose, organisationId, targets, title = 'Invite people', defaultNodeIds, onInvited }) => {
  const [form] = Form.useForm<InviteForm>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ManagedBy | null>(null);
  const rolesByNode = useRef(new Map<number, RoleName[]>());
  const emailsById = useRef(new Map<string, string>());
  const [rolesVersion, setRolesVersion] = useState(0);
  const [failures, setFailures] = useState<{ email: string; text: string }[]>([]);
  const [placeIds, setPlaceIds] = useState<number[]>([]);
  const [defaultRole, setDefaultRole] = useState<string | undefined>();
  const [overrides, setOverrides] = useState<Record<number, string | undefined>>({});
  const [changing, setChanging] = useState<Record<number, boolean>>({});
  const [pickingPlace, setPickingPlace] = useState(false);
  const people = Form.useWatch('people', form);
  const treeIndex = useSelector(selectTreeIndex);

  const loadRoles = useCallback(async (nodeId: number) => {
    const cached = rolesByNode.current.get(nodeId);
    if (cached) return cached;
    const list = (await getRoleNames(nodeId)).filter((role) => role.assignable !== false);
    rolesByNode.current.set(nodeId, list);
    return list;
  }, []);

  const targetById = useMemo(
    () => new Map(targets.map((target) => [target.id, target])),
    [targets]
  );
  const treeData = useMemo(() => {
    const toData = (node: PlaceTreeNode): any => ({
      value: node.value,
      name: node.name,
      disabled: !node.selectable,
      checkable: node.selectable,
      title: (
        <span className="flex items-center gap-2">
          <LevelChip level={node.kind as Level} compact />
          <span>{node.name}</span>
        </span>
      ),
      children: node.children.map(toData)
    });
    return buildPlaceTree(targets, treeIndex ?? {}).map(toData);
  }, [targets, treeIndex]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setConflict(null);
    setFailures([]);
    rolesByNode.current.clear();
    emailsById.current.clear();
    const wanted = defaultNodeIds?.length ? defaultNodeIds : targets.length === 1 ? [targets[0].id] : [];
    setPlaceIds(wanted.filter((id) => targetById.has(id)));
    setDefaultRole(undefined);
    setOverrides({});
    setChanging({});
    setPickingPlace(false);
    setRolesVersion((n) => n + 1);
    // Only when the popup opens: later target refreshes must not wipe the user's choices.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Targets that arrive after the popup opened: pre-fill only while nothing is chosen yet.
  useEffect(() => {
    if (!open) return;
    const wanted = defaultNodeIds?.length ? defaultNodeIds : targets.length === 1 ? [targets[0].id] : [];
    const fill = wanted.filter((id) => targetById.has(id));
    if (fill.length) setPlaceIds((prev) => (prev.length ? prev : fill));
  }, [open, targets, defaultNodeIds, targetById]);

  // Load the role list of every chosen place (cached), so the default and the overrides can be checked.
  useEffect(() => {
    let stale = false;
    placeIds.forEach((id) => {
      if (rolesByNode.current.has(id)) return;
      loadRoles(id).then(() => {
        if (!stale) setRolesVersion((n) => n + 1);
      }).catch(() => undefined);
    });
    return () => {
      stale = true;
    };
  }, [placeIds, loadRoles]);

  const offered = useCallback(
    (id: number) => rolesByNode.current.get(id)?.map(roleKey),
    // rolesVersion changes whenever the cache fills
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rolesVersion]
  );

  /** Every role offered on at least one chosen place, for the "same role for all" select. */
  const roleChoices = useMemo(() => {
    const seen = new Map<string, RoleName>();
    placeIds.forEach((id) => {
      (rolesByNode.current.get(id) ?? []).forEach((role) => {
        if (!seen.has(roleKey(role))) seen.set(roleKey(role), role);
      });
    });
    return Array.from(seen.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeIds, rolesVersion]);

  const roleNameOf = (id: number): string | undefined => {
    const key = effectiveRole(id, defaultRole, overrides);
    return (rolesByNode.current.get(id) ?? []).find((role) => roleKey(role) === key)?.name;
  };

  const summary = buildSummary(
    placeIds.map((id) => ({
      name: targetById.get(id)?.name ?? '',
      kind: targetById.get(id)?.kind ?? '',
      roleName: roleNameOf(id)
    }))
  );
  const missingRole = placesNeedingRole(placeIds, defaultRole, overrides, offered);
  const refusing = placesRefusingDefault(placeIds, defaultRole, offered);
  const rowIds = placesWithRow(placeIds, defaultRole, overrides, offered, changing);

  // Every chosen place's roles must be in before anything is sent, so no role is guessed.
  const rolesReady = placeIds.every((id) => rolesByNode.current.has(id));

  const submit = async (values: InviteForm) => {
    if (organisationId == null) return;
    if (!rolesReady) {
      setError('The roles of the chosen places are still loading.');
      return;
    }
    if (placeIds.length < 1) {
      setError('Choose at least one place.');
      return;
    }
    if (missingRole.length > 0) {
      setError(
        `Choose a role for: ${missingRole.map((id) => targetById.get(id)?.name ?? id).join(', ')}.`
      );
      return;
    }
    const seen = new Set<string>();
    const emails = (values.people ?? [])
      .map((value) => (typeof value === 'string' ? emailsById.current.get(value) ?? '' : value.email).trim())
      .filter((email) => {
        const key = email.toLowerCase();
        if (!email || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    const single = emails.length === 1;
    setSaving(true);
    setError(null);
    setConflict(null);
    setFailures([]);
    const grants = placeIds.map((nodeId) => {
      const key = effectiveRole(nodeId, defaultRole, overrides);
      const picked = (rolesByNode.current.get(nodeId) ?? []).find((role) => roleKey(role) === key);
      return picked?.custom_role_id != null
        ? { node_id: nodeId, custom_role_id: picked.custom_role_id }
        : { node_id: nodeId, role: key! };
    });
    const results: OrganisationInviteResult[] = [];
    const failed: { email: string; text: string }[] = [];
    try {
      for (const email of emails) {
        try {
          const result = await inviteToOrganisation(organisationId, {
            email,
            ...(single && values.name?.trim() ? {
              name: values.name.trim()
            } : {}),
            grants
          });
          results.push(result);
          if (single) {
            const outcome = issuedMessage(result);
            if (result.status === 'invitation_pending' && result.invitation_id != null) {
              const undo = async () => {
                try {
                  await revokeOrganisationInvitation(organisationId, result.invitation_id!);
                  message.success('Invitation revoked.');
                  onInvited?.(result);
                } catch (e: any) {
                  message.error(errorText(e));
                }
              };
              message.open({
                type: outcome.type,
                content: (
                  <span>
                    {outcome.text}{' '}
                    <Button
                      type="link"
                      size="small"
                      onClick={undo}
                    >Undo</Button>
                  </span>
                ),
                duration: 10
              });
            } else {
              message[outcome.type](outcome.text);
            }
          }
        } catch (e: any) {
          const managedBy = managedByFrom409(e);
          if (single && managedBy) {
            setConflict(managedBy);
            return;
          }
          const text = managedBy
            ? 'Managed by another level, so it cannot be changed here.'
            : errorText(e);
          if (single) {
            setError(text);
            return;
          }
          failed.push({ email, text });
        }
      }
      if (!single) {
        const invited = results.filter((r) => r.status === 'invitation_pending').length;
        const granted = results.length - invited;
        if (results.length > 0) {
          message.success(`${invited} invited, ${granted} already in the organisation were given access`);
        }
      }
      if (results.length > 0) onInvited?.(results[results.length - 1]);
      if (failed.length > 0) {
        setFailures(failed);
        form.setFieldsValue({ 
          people: failed.map((item) => ({ email: item.email })) 
        });
        return;
      }
      form.resetFields();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={640}
    >
      <ManagedByBanner managedBy={conflict} setting="user" />
      {error && <Alert
        className="mb-3"
        type="error"
        showIcon
        message={error} /
      >}
      <Form
        form={form}
        layout="vertical"
        onFinish={submit}
        initialValues={{ people: [] }}
      >
        <Form.Item
          label="Who"
          name="people"
          extra="Pick people or type an email address. Someone without an account gets one, and a sign-in email. Someone already in the organisation gets the access straight away."
          rules={[{
            validator: async (_, value?: PickerValue[]) => {
              const list = (value ?? []).map((item) =>
                (typeof item === 'string' ? emailsById.current.get(item) ?? item : item.email).trim());
              if (list.length < 1) throw new Error('Choose at least one person');
              const invalid = list.filter((email) => !EMAIL_PATTERN.test(email));
              if (invalid.length > 0) {
                throw new Error(`Not a valid email address: ${invalid.join(', ')}`);
              }
            }
          }]}
        >
          {organisationId != null ? (
            <PersonPicker
              mode="multiple"
              scope={{ 
                kind: 'node', 
                nodeId: organisationId, 
                purpose: 'invite' 
              }}
              allowNewEmail
              placeholder="Search by name or email, or type a new email address"
              onPersonKnown={(person) => emailsById.current.set(person.id, person.email)}
            />
          ) : (
            <Select mode="multiple" disabled />
          )}
        </Form.Item>
        {failures.length > 0 && (
          <Alert
            className="mb-3"
            type="error"
            showIcon
            message="These invitations failed"
            description={
              <ul className="m-0 pl-4">
                {failures.map((item) => (
                  <li key={item.email}>{item.email}: {item.text}</li>
                ))}
              </ul>
            }
          />
        )}
        {people?.length === 1 && (
          <Form.Item
            label="Name (optional)"
            name="name"
            rules={[{ max: 255 }]}
          >
            <Input />
          </Form.Item>
        )}
        <Form.Item label="Where" required>
          <TreeSelect
            treeCheckable
            treeCheckStrictly
            multiple
            treeDefaultExpandAll
            showCheckedStrategy={TreeSelect.SHOW_ALL}
            treeNodeFilterProp="name"
            treeData={treeData}
            value={placeIds.map((id) => ({ value: id }))}
            onChange={(next: { value: number }[]) => {
              const ids = (next ?? []).map((item) => item.value);
              setPlaceIds(ids);
              setOverrides((prev) => pruneOverrides(ids, prev));
              setChanging((prev) => pruneOverrides(ids, prev));
            }}
            placeholder="Organisation, entities and workspaces"
            style={{ width: '100%' }}
            treeLine={false}
          />
        </Form.Item>
        <Form.Item label="Role" required>
          <Select
            placeholder={placeIds.length ? 'Role for all chosen places' : 'Choose where first'}
            disabled={placeIds.length === 0}
            value={defaultRole}
            onChange={setDefaultRole}
            optionLabelProp="short"
            style={{ width: '100%' }}
            popupMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 320, maxWidth: 420 }}
            options={roleChoices.map((role) => ({
              value: roleKey(role),
              short: role.name,
              label: (
                <div>
                  <div>{role.name}</div>
                  {role.description && (
                    <div 
                      className="text-gray-400 text-xs whitespace-normal"
                    >{role.description}</div>
                  )}
                </div>
              )
            }))}
          />
        </Form.Item>
        {refusing.length > 0 && (
          <div className="mb-2 text-sm text-gray-500">
            {`This role is not available on ${refusing.map((id) => targetById.get(id)?.name ?? id).join(', ')}. Choose another role there.`}
          </div>
        )}
        {rowIds.length > 0 && (
          <div className="mb-2 flex flex-col gap-1">
            {rowIds.map((id) => {
              const target = targetById.get(id);
              if (!target) return null;
              return (
                <div key={id} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">{targetLabel(target)}</div>
                  <div style={{ width: 220 }}>
                    <RoleSelect
                      nodeId={id}
                      value={overrides[id]}
                      onChange={(role) => setOverrides((prev) => ({
                        ...prev,
                        [id]: role
                      }))}
                      loadRoles={loadRoles}
                    />
                  </div>
                  {!refusing.includes(id) && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => {
                        setOverrides((prev) => ({ ...prev, [id]: undefined }));
                        setChanging((prev) => ({ ...prev, [id]: false }));
                      }}
                    >Use default</Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {placeIds.length > 1 && defaultRole !== undefined && (
          pickingPlace ? (
            <Select
              autoFocus
              defaultOpen
              size="small"
              placeholder="Place to change"
              style={{ width: '100%' }}
              className="mb-2"
              onBlur={() => setPickingPlace(false)}
              onChange={(id: number) => {
                setOverrides((prev) => ({
                  ...prev,
                  [id]: startingOverride(id, defaultRole, offered)
                }));
                setChanging((prev) => ({ ...prev, [id]: true }));
                setPickingPlace(false);
              }}
              options={placeIds
                .filter((id) => !rowIds.includes(id))
                .map((id) => ({
                  value: id,
                  label: targetById.get(id)?.name ?? String(id)
                }))}
            />
          ) : (
            <Button
              type="link"
              size="small"
              className="mb-2 p-0"
              onClick={() => setPickingPlace(true)}
            >
              Change role for a place
            </Button>
          )
        )}
        {summary && <div 
          className="text-sm text-gray-600 mt-2"
        >{summary}</div>}
        <Button
          htmlType="submit"
          type="primary"
          block
          loading={saving}
          disabled={placeIds.length === 0 || !rolesReady}
          className="mt-4"
        >
          Send invitation
        </Button>
      </Form>
    </Modal>
  );
};

export default InviteModal;
